/**
 * Centralized Route Definitions
 * Single source of truth for all locale-specific URLs
 * 
 * Benefits:
 * - Type-safe route generation
 * - No duplicate URL logic across 45+ files
 * - Easy to maintain and update
 * - Prevents typos and inconsistencies
 */

import { defaultLocale, type Locale } from './i18n';

type LocaleMap = Partial<Record<Locale, string>> & { [k: string]: string | undefined };
export type RouteKey = keyof typeof ROUTES;

/**
 * All locale-specific routes in one place
 * Format: { en: '/english-url', nl: '/dutch-url', de: '/german-url' }
 */
export const ROUTES = {
  // ===== SOLUTIONS =====
  kiosk: {
    en: '/self-ordering-kiosk',
    nl: '/horeca-kiosk',
    de: '/bestellterminal-gastronomie',
  },
  qr: {
    en: '/qr-ordering',
    nl: '/qr-bestellen',
    de: '/qr-bestellung',
  },
  tablet: {
    en: '/tablet-ordering',
    nl: '/tablet-bestellen',
    de: '/tablet-bestellung',
  },
  webshop: {
    en: '/online-ordering-webshop',
    nl: '/online-bestellen-webshop',
    de: '/online-bestell-webshop',
  },
  ayce: {
    en: '/all-you-can-eat',
    nl: '/all-you-can-eat',
    de: '/all-you-can-eat',
  },

  // ===== CORE PAGES =====
  home: {
    en: '/',
    nl: '/',
    de: '/',
  },
  demo: {
    en: '/request-demo',
    nl: '/demo-aanvragen',
    de: '/demo-anfragen',
  },
  contact: {
    en: '/contact',
    nl: '/contact',
    de: '/kontakt',
  },
  thankYou: {
    en: '/thank-you',
    nl: '/bedankt',
    de: '/danke',
  },
  reviews: {
    en: '/reviews',
    nl: '/beoordelingen',
    de: '/bewertungen',
  },

  // ===== COLLECTIONS =====
  integrations: {
    en: '/integrations',
    nl: '/integraties',
    de: '/integrationen',
  },
  industries: {
    en: '/industries',
    nl: '/branches',
    de: '/branchen',
  },
  blog: {
    en: '/blog',
    nl: '/blog',
    de: '/blog',
  },

  // ===== LEGAL PAGES =====
  privacy: {
    en: '/privacy-policy',
    nl: '/privacy-verklaring',
    de: '/datenschutz',
  },
  terms: {
    en: '/terms-and-conditions',
    nl: '/algemene-voorwaarden',
    de: '/allgemeine-geschaeftsbedingungen',
  },
  cookies: {
    en: '/cookie-policy',
    nl: '/cookiebeleid',
    de: '/cookie-richtlinie',
  },

  // ===== INDUSTRY PAGES =====
  // Each industry has locale-specific slugs
  'industry-airports': {
    en: '/industries/airports',
    nl: '/branches/luchthavens',
    de: '/branchen/flughaefen',
  },
  'industry-all-you-can-eat': {
    en: '/industries/all-you-can-eat',
    nl: '/branches/all-you-can-eat',
    de: '/branchen/all-you-can-eat',
  },
  'industry-amusement-parks': {
    en: '/industries/amusement-parks',
    nl: '/branches/pretparken',
    de: '/branchen/freizeitparks',
  },
  'industry-bakeries': {
    en: '/industries/bakeries',
    nl: '/branches/bakkerijen',
    de: '/branchen/baeckereien',
  },
  'industry-beach-clubs': {
    en: '/industries/beach-clubs',
    nl: '/branches/strandtenten',
    de: '/branchen/strandclubs',
  },
  'industry-bowling-centers': {
    en: '/industries/bowling-centers',
    nl: '/branches/bowlingcentra',
    de: '/branchen/bowlingcenter',
  },
  'industry-cafes-bars': {
    en: '/industries/cafes-bars',
    nl: '/branches/cafes-bars',
    de: '/branchen/cafes-bars',
  },
  'industry-canteens': {
    en: '/industries/canteens',
    nl: '/branches/kantines',
    de: '/branchen/kantinen',
  },
  'industry-casinos': {
    en: '/industries/casinos',
    nl: '/branches/casinos',
    de: '/branchen/kasinos',
  },
  'industry-catering': {
    en: '/industries/catering',
    nl: '/branches/catering',
    de: '/branchen/catering',
  },
  'industry-concert-halls': {
    en: '/industries/concert-halls',
    nl: '/branches/concertzalen',
    de: '/branchen/konzertsaele',
  },
  'industry-congress-centers': {
    en: '/industries/congress-centers',
    nl: '/branches/congressen',
    de: '/branchen/kongresse',
  },
  'industry-cruise-ships': {
    en: '/industries/cruise-ships',
    nl: '/branches/cruiseschepen',
    de: '/branchen/kreuzfahrtschiffe',
  },
  'industry-events-festivals': {
    en: '/industries/events-festivals',
    nl: '/branches/events-festivals',
    de: '/branchen/events-festivals',
  },
  'industry-expos': {
    en: '/industries/expos',
    nl: '/branches/beurzen',
    de: '/branchen/messen',
  },
  'industry-fast-food': {
    en: '/industries/fast-food',
    nl: '/branches/fastfood',
    de: '/branchen/fastfood',
  },
  'industry-ferries': {
    en: '/industries/ferries',
    nl: '/branches/ferries',
    de: '/branchen/faehren',
  },
  'industry-food-halls': {
    en: '/industries/food-halls',
    nl: '/branches/foodhalls',
    de: '/branchen/food-courts',
  },
  'industry-food-trucks': {
    en: '/industries/food-trucks',
    nl: '/branches/foodtrucks',
    de: '/branchen/foodtrucks',
  },
  'industry-golf-courses': {
    en: '/industries/golf-courses',
    nl: '/branches/golfbanen',
    de: '/branchen/golfbahnen',
  },
  'industry-holiday-parks': {
    en: '/industries/holiday-parks',
    nl: '/branches/vakantieparken',
    de: '/branchen/ferienparks',
  },
  'industry-hospitals': {
    en: '/industries/hospitals',
    nl: '/branches/ziekenhuizen',
    de: '/branchen/krankenhaeuser',
  },
  'industry-hotels': {
    en: '/industries/hotels',
    nl: '/branches/hotels',
    de: '/branchen/hotels',
  },
  'industry-ice-cream-parlors': {
    en: '/industries/ice-cream-parlors',
    nl: '/branches/ijssalons',
    de: '/branchen/eisdielen',
  },
  'industry-lunchrooms': {
    en: '/industries/lunchrooms',
    nl: '/branches/lunchrooms',
    de: '/branchen/lunchrooms',
  },
  'industry-museums': {
    en: '/industries/museums',
    nl: '/branches/musea',
    de: '/branchen/museen',
  },
  'industry-restaurants': {
    en: '/industries/restaurants',
    nl: '/branches/restaurants',
    de: '/branchen/restaurants',
  },
  'industry-spa-wellness': {
    en: '/industries/spa-wellness',
    nl: '/branches/spa-wellness',
    de: '/branchen/spa-wellness',
  },
  'industry-stadiums': {
    en: '/industries/stadiums',
    nl: '/branches/stadions',
    de: '/branchen/stadien',
  },
  'industry-universities': {
    en: '/industries/universities',
    nl: '/branches/universiteiten',
    de: '/branchen/universitaeten',
  },
  'industry-zoos': {
    en: '/industries/zoos',
    nl: '/branches/dierentuinen',
    de: '/branchen/zoos',
  },
} as const;

/**
 * Safe route getter for new locales.
 * If a route isn't defined for a locale yet, we fall back to defaultLocale (en).
 */
export function routeFor(routeKey: RouteKey, locale: Locale): string {
  const map = ROUTES[routeKey] as unknown as LocaleMap;
  const v = map?.[locale];
  if (typeof v === 'string' && v.length) return v;
  const fallback = map?.[defaultLocale];
  if (typeof fallback === 'string' && fallback.length) return fallback;
  // last resort: try 'en'
  const en = map?.['en'];
  return typeof en === 'string' && en.length ? en : '/';
}

/**
 * Type-safe route getter
 * @param route - Route key from ROUTES object
 * @param locale - Current locale (en, nl, de)
 * @returns Localized path (e.g., '/horeca-kiosk' for kiosk + nl)
 * 
 * @example
 * getRoute('kiosk', 'nl') → '/horeca-kiosk'
 * getRoute('qr', 'en') → '/qr-ordering'
 */
export function getRoute(route: keyof typeof ROUTES, locale: Locale): string {
  // Fallback: if a new locale is partially configured, use the defaultLocale path
  // so pages still render (SEO slugs can be refined later).
  return (ROUTES[route] as any)?.[locale] ?? (ROUTES[route] as any)?.[defaultLocale] ?? '/';
}

/**
 * Get integration detail page path
 * @param slug - Integration slug (e.g., 'lightspeed')
 * @param locale - Current locale
 * @returns Full path (e.g., '/nl/integraties/lightspeed')
 */
export function getIntegrationPath(slug: string, locale: Locale): string {
  const basePath = locale === 'en' ? '' : `/${locale}`;
  return `${basePath}${ROUTES.integrations[locale]}/${slug}`;
}

/**
 * Get partner detail page path
 * @param slug - Partner slug (e.g., 'konacom')
 * @param locale - Current locale
 * @returns Full path (e.g., '/nl/partners/konacom')
 */
export function getPartnerPath(slug: string, locale: Locale): string {
  // Partners don't have translated paths - always use "partners"
  const basePath = locale === 'en' ? '' : `/${locale}`;
  return `${basePath}/partners/${slug}`;
}

/**
 * Get integration or partner path based on slug
 * Automatically determines if it's a partner or integration
 * @param slug - Integration/partner slug
 * @param locale - Current locale
 * @returns Full path to integration or partner page
 */
export function getIntegrationOrPartnerPath(slug: string, locale: Locale): string {
  // List of partners that should go to /partners/ instead of /integrations/
  const partners = [
    'konacom', 'possystems', 'cecon', 'janssens-kassasystemen', 'ms-pos',
    'adnamics', 'upta', 'kassanet-pieterse', 'opsomer', 'evers-kassasystemen',
    'stn', 'kassanet-nuus', 'bork', 'prestop', 'acrelec', 'qiox',
    'van-hessen'
  ];
  
  const isPartner = partners.includes(slug);
  return isPartner ? getPartnerPath(slug, locale) : getIntegrationPath(slug, locale);
}

/**
 * Get industry/branch detail page path
 * @param slug - Industry slug (e.g., 'hotels')
 * @param locale - Current locale
 * @returns Full path (e.g., '/branches/hotels')
 */
export function getIndustryPath(slug: string, locale: Locale): string {
  return `${ROUTES.industries[locale]}/${slug}`;
}

/**
 * Get blog post path
 * @param slug - Blog post slug
 * @param locale - Current locale
 * @returns Full path (e.g., '/blog/post-slug')
 */
export function getBlogPath(slug: string, locale: Locale): string {
  return `${ROUTES.blog[locale]}/${slug}`;
}

/**
 * Shorthand helpers for commonly used routes
 */
export const getKioskPath = (locale: Locale) => getRoute('kiosk', locale);
export const getQrPath = (locale: Locale) => getRoute('qr', locale);
export const getTabletPath = (locale: Locale) => getRoute('tablet', locale);
export const getWebshopPath = (locale: Locale) => getRoute('webshop', locale);
export const getDemoPath = (locale: Locale) => getRoute('demo', locale);
export const getContactPath = (locale: Locale) => getRoute('contact', locale);
export const getReviewsPath = (locale: Locale) => getRoute('reviews', locale);

/**
 * Get industry route key from English slug
 * @param slug - English industry slug (e.g., 'restaurants', 'hotels')
 * @returns Route key (e.g., 'industry-restaurants')
 */
export function getIndustryRouteKey(slug: string): keyof typeof ROUTES | null {
  const key = `industry-${slug}` as keyof typeof ROUTES;
  if (key in ROUTES) {
    return key;
  }
  return null;
}

/**
 * Get full industry path with correct locale-specific slug
 * @param slug - English industry slug
 * @param locale - Current locale
 * @returns Full localized path (e.g., '/branches/restaurants' for NL)
 */
export function getLocalizedIndustryPath(slug: string, locale: Locale): string {
  const routeKey = getIndustryRouteKey(slug);
  if (routeKey && ROUTES[routeKey]) {
    return ROUTES[routeKey][locale];
  }
  // Fallback to generic path
  return `${ROUTES.industries[locale]}/${slug}`;
}

